// Pure layout utilities for PDF form field positioning/sizing
// These functions must remain DOM- and React-agnostic.

export type Bounds = { 
  maxW: number; 
  maxH: number;
  width: number;
  height: number;
};
export type Guides = { x: number[]; y: number[] };

export type Position = { x0: number; y0: number; x1: number; y1: number; page?: number };
export type FieldLike = { position?: Position } & Record<string, any>;

export type SimpleRect = { left: number; right: number; top: number; bottom: number };

const clone = <T>(arr: T[]): T[] => arr.map(x => (typeof x === 'object' && x !== null ? { ...(x as any) } : x)) as T[];

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

export function nudgeFields<T extends FieldLike>(
  fields: T[], indices: number[], dx: number, dy: number, bounds: Bounds
): T[] {
  const next = clone(fields);
  const { maxW, maxH } = bounds;
  indices.forEach(i => {
    const f = next[i];
    if (!f || !f.position) return;
    const pos = f.position;
    const w = pos.x1 - pos.x0;
    const h = pos.y1 - pos.y0;
    const nx0 = clamp(pos.x0 + dx, 0, Math.max(0, maxW - w));
    const ny0 = clamp(pos.y0 + dy, 0, Math.max(0, maxH - h));
    (next[i] as any).position = { ...pos, x0: nx0, y0: ny0, x1: nx0 + w, y1: ny0 + h };
  });
  return next;
}

export function alignSelected<T extends FieldLike>(
  fields: T[], indices: number[], bounds: Bounds,
  mode: 'left'|'right'|'top'|'bottom'|'hcenter'|'vcenter'
): T[] {
  if (indices.length < 2) return fields;
  const next = clone(fields);
  const { maxW, maxH } = bounds;
  const anchor = indices[0];
  const a = next[anchor]; if (!a || !a.position) return fields;
  const ap = a.position;
  const ref = {
    left: ap.x0,
    right: ap.x1,
    top: ap.y0,
    bottom: ap.y1,
    cx: ap.x0 + (ap.x1 - ap.x0)/2,
    cy: ap.y0 + (ap.y1 - ap.y0)/2,
  };
  indices.forEach(i => {
    const f = next[i]; if (!f || !f.position) return;
    const w = f.position.x1 - f.position.x0;
    const h = f.position.y1 - f.position.y0;
    let x0 = f.position.x0, y0 = f.position.y0;
    if (mode === 'left') x0 = ref.left;
    if (mode === 'right') x0 = ref.right - w;
    if (mode === 'top') y0 = ref.top;
    if (mode === 'bottom') y0 = ref.bottom - h;
    if (mode === 'hcenter') x0 = Math.round(ref.cx - w/2);
    if (mode === 'vcenter') y0 = Math.round(ref.cy - h/2);
    x0 = clamp(x0, 0, Math.max(0, (maxW - w)));
    y0 = clamp(y0, 0, Math.max(0, (maxH - h)));
    (next[i] as any).position = { ...f.position, x0, y0, x1: x0 + w, y1: y0 + h };
  });
  return next;
}

export function distributeSelected<T extends FieldLike>(
  fields: T[], indices: number[], bounds: Bounds, axis: 'h'|'v'
): T[] {
  if (indices.length < 3) return fields;
  const next = clone(fields);
  const { maxW, maxH } = bounds;
  const items = indices
    .map(i => ({ i, f: next[i] }))
    .filter(o => o.f && o.f.position) as { i:number; f:T }[];
  if (items.length < 3) return fields;

  if (axis === 'h') {
    const sorted = items.slice().sort((a,b) => (a.f.position!.x0 - b.f.position!.x0));
    const first = sorted[0].f.position!;
    const last = sorted[sorted.length - 1].f.position!;
    const innerSpan = (last.x1 - first.x0);
    const widths = sorted.map(o => (o.f.position!.x1 - o.f.position!.x0));
    const totalW = widths.reduce((s,w)=>s+w,0);
    const gaps = sorted.length - 1;
    const gap = gaps > 0 ? (innerSpan - totalW) / gaps : 0;
    let cursor = first.x0;
    sorted.forEach((o, sidx) => {
      const pos = (next[o.i] as any).position as Position;
      const w = pos.x1 - pos.x0;
      const x0 = Math.round(sidx === 0 ? pos.x0 : cursor);
      const y0 = pos.y0;
      const clampedX0 = clamp(x0, 0, Math.max(0, maxW - w));
      (next[o.i] as any).position = { ...pos, x0: clampedX0, y0, x1: clampedX0 + w, y1: y0 + (pos.y1 - pos.y0) };
      cursor = clampedX0 + w + gap;
    });
    // Snap last item to original right edge
    const lastIdx = sorted[sorted.length - 1].i;
    const lp = (next[lastIdx] as any).position as Position;
    const lw = lp.x1 - lp.x0;
    const targetX1 = last.x1;
    const lx0 = clamp(targetX1 - lw, 0, Math.max(0, maxW - lw));
    (next[lastIdx] as any).position = { ...lp, x0: lx0, x1: lx0 + lw };
  } else {
    const sorted = items.slice().sort((a,b) => (a.f.position!.y0 - b.f.position!.y0));
    const first = sorted[0].f.position!;
    const last = sorted[sorted.length - 1].f.position!;
    const innerSpan = (last.y1 - first.y0);
    const heights = sorted.map(o => (o.f.position!.y1 - o.f.position!.y0));
    const totalH = heights.reduce((s,h)=>s+h,0);
    const gaps = sorted.length - 1;
    const gap = gaps > 0 ? (innerSpan - totalH) / gaps : 0;
    let cursor = first.y0;
    sorted.forEach((o, sidx) => {
      const pos = (next[o.i] as any).position as Position;
      const h = pos.y1 - pos.y0;
      const y0 = Math.round(sidx === 0 ? pos.y0 : cursor);
      const x0 = pos.x0;
      const clampedY0 = clamp(y0, 0, Math.max(0, maxH - h));
      (next[o.i] as any).position = { ...pos, x0, y0: clampedY0, x1: x0 + (pos.x1 - pos.x0), y1: clampedY0 + h };
      cursor = clampedY0 + h + gap;
    });
    // Snap last to original bottom edge
    const lastIdx = sorted[sorted.length - 1].i;
    const lp = (next[lastIdx] as any).position as Position;
    const lh = lp.y1 - lp.y0;
    const targetY1 = last.y1;
    const ly0 = clamp(targetY1 - lh, 0, Math.max(0, maxH - lh));
    (next[lastIdx] as any).position = { ...lp, y0: ly0, y1: ly0 + lh };
  }
  return next;
}

export function equalizeSelected<T extends FieldLike>(
  fields: T[], indices: number[], bounds: Bounds, mode: 'w'|'h'
): T[] {
  if (indices.length < 2) return fields;
  const next = clone(fields);
  const { maxW, maxH } = bounds;
  const refIdx = indices[0];
  const refField = next[refIdx];
  if (!refField || !refField.position) return fields;
  const refW = refField.position.x1 - refField.position.x0;
  const refH = refField.position.y1 - refField.position.y0;
  indices.forEach(i => {
    const f = next[i]; if (!f || !f.position) return;
    const pos = f.position;
    const x0 = pos.x0;
    const y0 = pos.y0;
    let w = pos.x1 - pos.x0;
    let h = pos.y1 - pos.y0;
    if (mode === 'w') {
      w = Math.max(1, refW);
      w = Math.min(w, Math.max(1, maxW - x0));
    } else {
      h = Math.max(1, refH);
      h = Math.min(h, Math.max(1, maxH - y0));
    }
    (next[i] as any).position = { ...pos, x0, y0, x1: x0 + w, y1: y0 + h };
  });
  return next;
}

export function fitSelectedToFrame<T extends FieldLike>(
  fields: T[], indices: number[], bounds: Bounds, guides: Guides, snapRange: number, inset: number = 0, supportRects?: SimpleRect[]
): { fields: T[]; changed: boolean; snapped: boolean } {
  const next = clone(fields);
  const { maxW, maxH } = bounds;
  let snapped = false;
  let changed = false;

  const SUPPORT_TOL = 2;
  const SUPPORT_COVERAGE = 0.7;

  const overlapLen = (a0: number, a1: number, b0: number, b1: number) => Math.max(0, Math.min(a1, b1) - Math.max(a0, b0));

  const hasVerticalSupport = (x: number, y0: number, y1: number) => {
    if (!supportRects || supportRects.length === 0) return true;
    const span = Math.max(1, y1 - y0);
    return supportRects.some(r => {
      const near = Math.abs(r.left - x) <= SUPPORT_TOL || Math.abs(r.right - x) <= SUPPORT_TOL;
      if (!near) return false;
      const ov = overlapLen(r.top, r.bottom, y0, y1);
      return (ov / span) >= SUPPORT_COVERAGE;
    });
  };

  const hasHorizontalSupport = (y: number, x0: number, x1: number) => {
    if (!supportRects || supportRects.length === 0) return true;
    const span = Math.max(1, x1 - x0);
    return supportRects.some(r => {
      const near = Math.abs(r.top - y) <= SUPPORT_TOL || Math.abs(r.bottom - y) <= SUPPORT_TOL;
      if (!near) return false;
      const ov = overlapLen(r.left, r.right, x0, x1);
      return (ov / span) >= SUPPORT_COVERAGE;
    });
  };

  const findLeftGuide = (x0: number, y0: number, y1: number) => {
    const maxX = x0 + snapRange;
    for (let i = guides.x.length - 1; i >= 0; i--) {
      const v = guides.x[i];
      if (v > maxX) continue;
      if (hasVerticalSupport(v, y0, y1)) return v;
    }
    return null;
  };

  const findRightGuide = (x1: number, y0: number, y1: number) => {
    const minX = x1 - snapRange;
    for (let i = 0; i < guides.x.length; i++) {
      const v = guides.x[i];
      if (v < minX) continue;
      if (hasVerticalSupport(v, y0, y1)) return v;
    }
    return null;
  };

  const findTopGuide = (y0: number, x0: number, x1: number) => {
    const maxY = y0 + snapRange;
    for (let i = guides.y.length - 1; i >= 0; i--) {
      const v = guides.y[i];
      if (v > maxY) continue;
      if (hasHorizontalSupport(v, x0, x1)) return v;
    }
    return null;
  };

  const findBottomGuide = (y1: number, x0: number, x1: number) => {
    const minY = y1 - snapRange;
    for (let i = 0; i < guides.y.length; i++) {
      const v = guides.y[i];
      if (v < minY) continue;
      if (hasHorizontalSupport(v, x0, x1)) return v;
    }
    return null;
  };

  indices.forEach(i => {
    const f = next[i]; if (!f || !f.position) return;
    const pos = f.position as Position;
    let { x0, y0, x1, y1 } = pos;
    const L = findLeftGuide(x0, y0, y1);
    const R = findRightGuide(x1, y0, y1);
    const T = findTopGuide(y0, x0, x1);
    const B = findBottomGuide(y1, x0, x1);
    if (L !== null) { x0 = L; snapped = true; }
    if (R !== null) { x1 = R; snapped = true; }
    if (T !== null) { y0 = T; snapped = true; }
    if (B !== null) { y1 = B; snapped = true; }
    if (x1 <= x0) x1 = x0 + 1;
    if (y1 <= y0) y1 = y0 + 1;
    // Initial clamp after snapping to guides
    let nx0 = clamp(x0, 0, Math.max(0, maxW - 1));
    let ny0 = clamp(y0, 0, Math.max(0, maxH - 1));
    let nx1 = clamp(x1, 1, maxW);
    let ny1 = clamp(y1, 1, maxH);

    // Fit exactly to opposing guides with optional inset; otherwise apply a generic shrink
    const w = nx1 - nx0;
    const h = ny1 - ny0;
    const shrink = Math.max(0, inset || 0);
    const minWidth = 2 * shrink + 0.1;
    const minHeight = 2 * shrink + 0.1;

    if (L !== null && R !== null) {
      const tx0 = L + shrink;
      const tx1 = R - shrink;
      if (tx1 > tx0) {
        nx0 = clamp(tx0, 0, Math.max(0, maxW - 1));
        nx1 = clamp(tx1, 1, maxW);
      } else {
        // fallback to a minimal width centered in the frame
        const mid = (L + R) / 2;
        nx0 = clamp(mid - 0.5, 0, Math.max(0, maxW - 1));
        nx1 = clamp(nx0 + 1, 1, maxW);
      }
    } else if (shrink > 0 && (nx1 - nx0 > minWidth)) {
      nx0 += shrink;
      nx1 -= shrink;
    }

    if (T !== null && B !== null) {
      const ty0 = T + shrink;
      const ty1 = B - shrink;
      if (ty1 > ty0) {
        ny0 = clamp(ty0, 0, Math.max(0, maxH - 1));
        ny1 = clamp(ty1, 1, maxH);
      } else {
        const mid = (T + B) / 2;
        ny0 = clamp(mid - 0.5, 0, Math.max(0, maxH - 1));
        ny1 = clamp(ny0 + 1, 1, maxH);
      }
    } else if (shrink > 0 && (ny1 - ny0 > minHeight)) {
      ny0 += shrink;
      ny1 -= shrink;
    }
    
    if (nx0 !== pos.x0 || ny0 !== pos.y0 || nx1 !== pos.x1 || ny1 !== pos.y1) changed = true;
    (next[i] as any).position = { ...pos, x0: nx0, y0: ny0, x1: nx1, y1: ny1 };
  });

  return { fields: next, changed, snapped };
}

export function createNewField(x0: number, y0: number, x1: number, y1: number, existingFields: FieldLike[]): FieldLike {
  const fieldNumber = existingFields.length + 1;
  return {
    name: `field_${fieldNumber}`,
    label: `Field ${fieldNumber}`,
    type: 'text',
    defaultValue: '',
    position: {
      x0: Math.min(x0, x1),
      y0: Math.min(y0, y1),
      x1: Math.max(x0, x1),
      y1: Math.max(y0, y1),
      page: 1
    },
    fontsize: 12,
  };
}

export function computeGuidesFromRects(rects: SimpleRect[], formRect: SimpleRect): Guides {
  const xs = new Set<number>();
  const ys = new Set<number>();
  rects.forEach(r => {
    // Convert absolute DOM coords to local (form) coords and round to integer px
    const q = (v: number) => Math.round(v * 2) / 2; // quantize to 0.5px grid
    xs.add(q(r.left - formRect.left));
    xs.add(q(r.right - formRect.left));
    ys.add(q(r.top - formRect.top));
    ys.add(q(r.bottom - formRect.top));
  });
  const x = Array.from(xs).sort((a,b)=>a-b);
  const y = Array.from(ys).sort((a,b)=>a-b);
  return { x, y };
}
